import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import DataTable from '../../components/ui/DataTable'
import DateRangeFilter from '../../components/ui/DateRangeFilter'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { GRANTS } from '../../auth/grants'
import { useGrants } from '../../auth/useCurrentUser'
import { searchTerm } from '../../lib/searchTerm'
import { exportSentEmails } from './api/sentEmailApi'
import { sentEmailColumns } from './components/SentEmailColumns'
import SentEmailDetailDialog from './components/SentEmailDetailDialog'
import { useSentEmails } from './hooks/useSentEmails'
import type { SentEmailListItem } from './types'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import { Paginator, type PaginatorPageChangeEvent } from 'primereact/paginator'
import { formatDateTime } from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { statusTone } from './components/SentEmailColumns'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import './admin.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/** Arama debounce süresi (anayasa Page Standards ~400 ms). */
const DEBOUNCE_MS = 400

/**
 * "Tümü" seçeneğinin değeri. Boş-OLMAYAN bir sentinel gerekir: PrimeReact Dropdown, `null`/`''`
 * değerli seçeneği "boş seçim" sayıp `onChange`'i yutar (`OperationCodesAdminPage`'de `ALL_MODULES`
 * ile belgelenmiştir). Negatif değer seçildi çünkü `REFERANS.ID` daima pozitiftir → gerçek bir durumla
 * çakışamaz. API'ye giderken `null`'a (filtresiz) çevrilir.
 */
const ALL_STATUSES = -1

/** İşlem Kodu combosunun "Tümü" sentinel'i — {@link ALL_STATUSES} ile aynı gerekçe. */
const ALL_OPERATION_CODES = -1

/** Bildirim Tipi combosunun "Tümü" sentinel'i — {@link ALL_STATUSES} ile aynı gerekçe. */
const ALL_NOTIFICATION_TYPES = -1

/** Boş tarih aralığı — iki filtre de aynı biçimi kullanır. */
const NO_RANGE = { from: null as string | null, to: null as string | null }

/**
 * Mobil kart aksiyon alanı — yalnız Detay varsa doğrudan gösterilir; "Deneme Sayısını Sıfırla" da
 * uygunsa (grant + `retryResettable`) ikisi kebab'a toplanır (v2 madde 1).
 */
function SentEmailCardActions({
  row,
  busy,
  canResetRow,
  onDetail,
  onResetRetry,
  t,
}: Readonly<{
  row: SentEmailListItem
  busy: boolean
  canResetRow: boolean
  onDetail: (id: number) => void
  onResetRetry: (row: SentEmailListItem) => void
  t: (key: string) => string
}>) {
  const menuRef = useRef<Menu>(null)
  if (!canResetRow) {
    return (
      <Button
        icon="pi pi-eye"
        rounded
        text
        aria-label={t('admin.sentEmails.actions.detail')}
        onClick={() => onDetail(row.id)}
      />
    )
  }
  const items: MenuItem[] = [
    { label: t('admin.sentEmails.actions.detail'), icon: 'pi pi-eye', command: () => onDetail(row.id) },
    {
      label: t('admin.sentEmails.actions.resetRetry'),
      icon: 'pi pi-replay',
      className: 'ui-card-menu-item--danger',
      command: () => onResetRetry(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        aria-label={t('admin.sentEmails.actions.menu')}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/**
 * Admin → **Bildirimler** (078). `SEND_ITEM`'ın **üç kanalı** (60 Eposta · 62 PN · 63 Web) için
 * **salt-okunur** izleme ekranı: sayfalı liste + filtreler + detay modalı + Excel dışa aktarma.
 *
 * <p><b>Kapsam ada yetişti</b> (kullanıcı kararı, 2026-08-07): 2026-08-06'da yalnız menü/başlık
 * etiketi "Bildirimler"e çevrilmiş ama sorgu e-postaya (tip 60) sabit kalmıştı. Artık kanal sabiti
 * yoktur; `NOTIFICATION_TYPE_ID` hem **filtre** hem de tablo **sütunudur**. Dosya/uç adlarındaki
 * `sentEmail` öneki korundu — yol (`/management/sent-emails`) yayınlanmış bir sözleşmedir.
 *
 * <p>Bilinçli olarak **yok**: "Yeni ekle", "Düzenle", "Sil", durum toggle'ı ve kolon sıralaması (spec
 * FR-001/FR-004). Kayıtları uygulama üretir (`NotificationDispatcher`), gönderim durumunu dış geçit
 * **Notifigate** ilerletir.
 *
 * <p><b>Tek yazma eylemi (078-b): "Deneme Sayısını Sıfırla"</b> — satır aksiyonu, kaydı Notifigate'in
 * kuyruğuna geri koyar (sayaç 0 + durum "Gönderilecek"; gönderim zamanı ve hata mesajı temizlenir).
 * Ekran **gönderim yapmaz**, yalnız kaydı yeniden denenebilir duruma getirir. Yıkıcı sayılır (alıcıya
 * yeni bir bildirim gidebilir) → anayasa gereği **onay adımı** vardır. Yetki: `SENT_EMAIL_MANAGE`;
 * yoksa buton hiç render edilmez.
 *
 * <p><b>İlk yüklemede varsayılan tarih penceresi UYGULANMAZ</b> (spec FR-005): tüm geçmiş, en yeni
 * üstte, sunucu-taraflı sayfalı gelir.
 *
 * <p>Filtreler VE mantığıyla birleşir ve <b>herhangi biri değişince sayfa 1'e döner</b> — aksi halde
 * kullanıcı daralttığı sonuç kümesinin var olmayan 4. sayfasında boş bir tablo görürdü.
 *
 * <p>Yetki: `SENT_EMAIL_VIEW`.
 */
export default function SentEmailsAdminPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  const canReset = hasGrant(GRANTS.SENT_EMAIL_MANAGE)

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [notificationTypeId, setNotificationTypeId] = useState<number>(ALL_NOTIFICATION_TYPES)
  const [sendStatusId, setSendStatusId] = useState<number>(ALL_STATUSES)
  const [operationCodeId, setOperationCodeId] = useState<number>(ALL_OPERATION_CODES)
  const [minRetryCount, setMinRetryCount] = useState<number | null>(null)
  const [plannedRange, setPlannedRange] = useState(NO_RANGE)
  const [sendingRange, setSendingRange] = useState(NO_RANGE)
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)

  /** Detayı açık kaydın kimliği; `null` = modal kapalı (ve istek atılmaz). */
  const [detailId, setDetailId] = useState<number | null>(null)

  /** Sıfırlama isteği süren satır — yalnız o satırın butonu kilitlenir (059 deseni). */
  const [busyId, setBusyId] = useState<number | null>(null)

  /**
   * Arama debounce'u + sayfa sıfırlama. Cleanup, ekran kapandıktan sonra tetiklenmeyi de önler
   * (kaynak §9.12 tuzağı).
   *
   * <p>`mounted` bayrağı ilk çalıştırmayı **atlar**: efekt mount'ta da koşar ve 400 ms sonra
   * `setFirst(0)` yapardı — kullanıcı o aralıkta 2. sayfaya geçtiyse sayfa kendiliğinden 1'e
   * dönerdi. Arama hiç değişmediği için o ilk çağrının zaten yapacak işi yok.
   */
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    const id = setTimeout(() => {
      setDebounced(search)
      setFirst(0)
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  const filters = useMemo(
    () => ({
      q: searchTerm(debounced),
      // "Tümü" → filtresiz (API'ye ilgili parametre hiç gönderilmez).
      notificationTypeId:
        notificationTypeId === ALL_NOTIFICATION_TYPES ? null : notificationTypeId,
      sendStatusId: sendStatusId === ALL_STATUSES ? null : sendStatusId,
      operationCodeId: operationCodeId === ALL_OPERATION_CODES ? null : operationCodeId,
      minRetryCount,
      plannedStart: plannedRange.from,
      plannedEnd: plannedRange.to,
      sendingStart: sendingRange.from,
      sendingEnd: sendingRange.to,
    }),
    [
      debounced,
      notificationTypeId,
      sendStatusId,
      operationCodeId,
      minRetryCount,
      plannedRange,
      sendingRange,
    ],
  )

  const { list, notificationTypes, sendStatuses, operationCodes, resetRetry } = useSentEmails({
    ...filters,
    page: Math.floor(first / rows),
    size: rows,
  })

  /**
   * "Deneme Sayısını Sıfırla" — kaydı dış geçidin kuyruğuna geri koyar (078-b).
   *
   * <p>Anayasa gereği **onay adımı** vardır: eylem geri alınamaz ve zaten "Gönderildi" durumundaki bir
   * kaydı sıfırlamak alıcıya **ikinci bir bildirim** gönderilmesine yol açabilir. Onay metni bunu
   * açıkça söyler ve alıcıyı gösterir, böylece kullanıcı yanlış satıra bastığını fark eder.
   *
   * <p>Hata toast'ını merkezî hata yönetimi (019) basar; ekran yalnız satır kilidini çözer.
   */
  const onResetRetry = (row: SentEmailListItem) => {
    confirmDialog({
      header: t('admin.sentEmails.confirm.resetRetryTitle'),
      message: t('admin.sentEmails.confirm.resetRetryMessage', { recipient: row.contactInformation }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.sentEmails.confirm.accept'),
      accept: () => {
        setBusyId(row.id)
        resetRetry.mutate(row.id, {
          onSuccess: () =>
            toast.show({
              severity: 'success',
              summary: t('admin.sentEmails.toast.retryReset'),
              life: 3000,
            }),
          onSettled: () => setBusyId(null),
        })
      },
    })
  }

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  // Bildirim tipi seçenekleri de aynı desende: "Tümü" başta, kanallar `REFERANS`'tan (aktif).
  // Sabit 60/62/63 listesi burada YOKTUR — analist bir kanal eklerse combo kendiliğinden genişler.
  const notificationTypeOptions = useMemo(
    () => [
      { label: t('admin.sentEmails.filter.notificationTypeAll'), value: ALL_NOTIFICATION_TYPES },
      ...(notificationTypes.data ?? []).map((n) => ({ label: n.name, value: n.id })),
    ],
    [notificationTypes.data, t],
  )

  // "Tümü" listenin BAŞINDA, istemcide üretilir; referans kimlikleri koda gömülmez.
  const statusOptions = useMemo(
    () => [
      { label: t('admin.sentEmails.filter.statusAll'), value: ALL_STATUSES },
      ...(sendStatuses.data ?? []).map((s) => ({ label: s.name, value: s.id })),
    ],
    [sendStatuses.data, t],
  )

  // İşlem kodları da aynı desende: "Tümü" başta, kodlar sunucudan (Bildirimler modülü, aktif).
  const operationCodeOptions = useMemo(
    () => [
      { label: t('admin.sentEmails.filter.operationCodeAll'), value: ALL_OPERATION_CODES },
      ...(operationCodes.data ?? []).map((c) => ({ label: c.name, value: c.id })),
    ],
    [operationCodes.data, t],
  )

  /**
   * "Filtre uygulanmış mı?" — hem Temizle butonunun görünürlüğünü hem de boş-durum metnini besler.
   *
   * <p>Aramada ham `search` **değil** `searchTerm(search)` ölçülür: kutu 2 karakterin altında
   * sunucuya hiçbir şey göndermez, dolayısıyla tek harf yazıldığında fiilen filtre yoktur. Ham
   * değere bakmak, hiçbir daraltma olmadığı hâlde "Filtreye uyan e-posta bulunamadı" dedirtirdi.
   */
  const hasFilter =
    searchTerm(search) !== '' ||
    notificationTypeId !== ALL_NOTIFICATION_TYPES ||
    sendStatusId !== ALL_STATUSES ||
    operationCodeId !== ALL_OPERATION_CODES ||
    minRetryCount != null ||
    !!plannedRange.from ||
    !!plannedRange.to ||
    !!sendingRange.from ||
    !!sendingRange.to

  const clearAll = () => {
    setSearch('')
    // `debounced`'ı da hemen boşalt: aksi halde 400 ms boyunca eski arama sunucuya gitmeye devam eder.
    setDebounced('')
    setNotificationTypeId(ALL_NOTIFICATION_TYPES)
    setSendStatusId(ALL_STATUSES)
    setOperationCodeId(ALL_OPERATION_CODES)
    setMinRetryCount(null)
    setPlannedRange(NO_RANGE)
    setSendingRange(NO_RANGE)
    setFirst(0)
  }

  /** Her filtre değişimi ilk sayfaya döner (spec FR-006). */
  const onFilterChange = (apply: () => void) => {
    apply()
    setFirst(0)
  }

  return (
    // `admin-sent-emails`: tarih inputlarının genişlik override'ını BU ekrana sınırlar (admin.css).
    <div className="admin-page admin-sent-emails">
      <ConfirmDialog />
      <SentEmailDetailDialog id={detailId} onHide={() => setDetailId(null)} />

      <PageHeader
        title={t('nav.admin.sentEmails')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA (kullanici istegi 2026-08-26). Onceden filtre
                cubugundaydi (`FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton
              onExport={() => exportSentEmails(filters)}
              disabled={items.length === 0}
            />
          </>
        }
      />

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('admin.sentEmails.filter.searchPlaceholder')}
        searchAriaLabel={t('admin.sentEmails.filter.searchPlaceholder')}
        onSearch={setSearch}
        showClear={hasFilter}
        clearLabel={t('admin.sentEmails.filter.clear')}
        onClear={clearAll}
      >
        {/*
          Bildirim Tipi, Durum ve İşlem Kodu comboları KENDİ satırında, sayı/tarih grubunun ÜSTÜNDE
          durur (kullanıcı isteği). "Tümü" açık bir seçenek olduğundan `showClear` (X) kullanılmaz —
          aynı işi yapan iki kontrol olmaz ve seçim hiçbir zaman boşa düşmez. aria-label kontrolün NE
          olduğunu söyler.

          Sıra kabadan inceye gider: önce KANAL (üç değerli, en geniş kırılım), sonra o kanal
          içindeki gönderim durumu, en sonda olayı belirleyen işlem kodu.
        */}
        <Dropdown
          value={notificationTypeId}
          options={notificationTypeOptions}
          onChange={(e) =>
            onFilterChange(() =>
              setNotificationTypeId((e.value as number | null) ?? ALL_NOTIFICATION_TYPES),
            )
          }
          aria-label={t('admin.sentEmails.filter.notificationTypeLabel')}
          disabled={notificationTypes.isError}
          className="admin-sent-email-type"
        />
        <Dropdown
          value={sendStatusId}
          options={statusOptions}
          onChange={(e) =>
            onFilterChange(() => setSendStatusId((e.value as number | null) ?? ALL_STATUSES))
          }
          aria-label={t('admin.sentEmails.filter.statusLabel')}
          disabled={sendStatuses.isError}
          className="admin-sent-email-status"
        />
        {/*
          İşlem Kodu combosu. Durum combosuyla aynı desen; tek fark GENİŞLİK: seçenek adları uzundur
          ("Toplantı tutanağı yayınlandı"). Dropdown `filter`'ı bilinçli olarak KAPALI — 059'un aynı
          katalogtan beslenen combosu da arama kutusu taşımaz (aynı kaynak, aynı boy: ~13 kayıt).
        */}
        <Dropdown
          value={operationCodeId}
          options={operationCodeOptions}
          onChange={(e) =>
            onFilterChange(() =>
              setOperationCodeId((e.value as number | null) ?? ALL_OPERATION_CODES),
            )
          }
          aria-label={t('admin.sentEmails.filter.operationCodeLabel')}
          disabled={operationCodes.isError}
          // 059'un AYNI katalogtan beslenen combosuyla aynı ölçü: PrimeReact varsayılanı 200px'tir ve
          // 11 kaydın ~5'ini gösterir → kullanıcı listenin sonundaki 813'ü görmek için kaydırmak
          // zorunda kalır. 28rem katalogun tamamını tek bakışta gösterir.
          scrollHeight="28rem"
          className="admin-sent-email-operation"
        />
        {/*
          Satır kesici (flexbox `flex-basis: 100%` hilesi): kendisinden sonrasını yeni satıra iter.
          Tek bir kesme yeter, çünkü ardındaki filtreler zaten TEK bir grup öğesidir.
        */}
        <span className="admin-filter-break" aria-hidden />
        {/*
          Sayı + tarih filtreleri TEK bir gruba sarılır (`flex-wrap: nowrap`) — çubuğun kendisi
          `flex-wrap: wrap` olduğundan, tek tek çocuk olarak dursalardı sarma noktası pencere
          genişliğine göre değişir ve kutular birbirinden kopardı (sayı kutusu tarihlerin bir üst
          satırına düşüyordu). Grup tek bir flex öğesi olduğu için sıraları hiçbir genişlikte
          bozulmaz. Sıra: Min. deneme → Planlanan → Gönderim.
        */}
        <div className="admin-filters-row">
          {/*
            `InputNumber` değeri temizlendiğinde `null` verir → filtre kalkar. **0 geçerli bir
            değerdir** ve `null`'dan ayrı tutulur (kaynak §9.5 tuzağı); api katmanı da `?? undefined`
            ile bu ayrımı korur.
          */}
          <InputNumber
            value={minRetryCount}
            onValueChange={(e) => onFilterChange(() => setMinRetryCount(e.value ?? null))}
            min={0}
            useGrouping={false}
            placeholder={t('admin.sentEmails.filter.minRetryPlaceholder')}
            aria-label={t('admin.sentEmails.filter.minRetryLabel')}
            className="admin-sent-email-retry"
          />
          <DateRangeFilter
            from={plannedRange.from}
            to={plannedRange.to}
            onChange={(range) => onFilterChange(() => setPlannedRange(range))}
            fromLabel={t('admin.sentEmails.filter.plannedFrom')}
            toLabel={t('admin.sentEmails.filter.plannedTo')}
          />
          <DateRangeFilter
            from={sendingRange.from}
            to={sendingRange.to}
            onChange={(range) => onFilterChange(() => setSendingRange(range))}
            fromLabel={t('admin.sentEmails.filter.sendingFrom')}
            toLabel={t('admin.sentEmails.filter.sendingTo')}
          />
        </div>
      </FilterBar>

      {/* Sunucu-taraflı sayfalama (lazy). Kolon sıralaması kapalı → sortField/onSort verilmez. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              icon="pi pi-bell"
              title={
                hasFilter
                  ? t('admin.sentEmails.emptyFiltered.title')
                  : t('admin.sentEmails.empty.title')
              }
              description={
                hasFilter
                  ? t('admin.sentEmails.emptyFiltered.desc')
                  : t('admin.sentEmails.empty.desc')
              }
            />
          )}
          {!list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <Badge tone={statusTone(row.sendStatusId)}>{row.sendStatusName ?? ''}</Badge>
                  <div className="ui-card-actions ui-card-actions--compact">
                    <SentEmailCardActions
                      row={row}
                      busy={busyId === row.id}
                      canResetRow={canReset && row.retryResettable}
                      onDetail={setDetailId}
                      onResetRetry={onResetRetry}
                      t={t}
                    />
                  </div>
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{row.contactInformation}</span>
                </div>
                <span className="ui-card-sub">{row.subject ?? ''}</span>
                <span className="ui-card-row">{row.notificationTypeName ?? ''}</span>
                {row.plannedSendingTime && (
                  <span className="ui-card-row">
                    {t('admin.sentEmails.columns.planned')}: {formatDateTime(row.plannedSendingTime)}
                  </span>
                )}
                {row.sendingTime && (
                  <span className="ui-card-row">
                    {t('admin.sentEmails.columns.sending')}: {formatDateTime(row.sendingTime)}
                  </span>
                )}
                {row.errorMessage && (
                  <span className="ui-card-row">
                    <i className="pi pi-exclamation-triangle admin-error-flag" /> {row.errorMessage}
                  </span>
                )}
              </div>
            ))}
          {!list.isLoading && total > rows && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e: PaginatorPageChangeEvent) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="ui-cards-paginator"
            />
          )}
        </div>
      ) : (
      <DataTable
        value={items}
        loading={list.isLoading}
        lazy
        first={first}
        rows={rows}
        totalRecords={total}
        onPage={(e) => {
          setFirst(e.first)
          setRows(e.rows)
        }}
        emptyMessage={
          <EmptyState
            icon="pi pi-bell"
            title={
              hasFilter
                ? t('admin.sentEmails.emptyFiltered.title')
                : t('admin.sentEmails.empty.title')
            }
            description={
              hasFilter
                ? t('admin.sentEmails.emptyFiltered.desc')
                : t('admin.sentEmails.empty.desc')
            }
          />
        }
      >
        {sentEmailColumns({
          t,
          onDetail: (row) => setDetailId(row.id),
          // Yetki yoksa `undefined` geçilir → buton hiç render edilmez (disabled değil).
          onResetRetry: canReset ? onResetRetry : undefined,
          busyId,
        })}
      </DataTable>
      )}
    </div>
  )
}
